import * as THREE from "three";
import type {
  CatalogDictionary,
  CatalogSurfaceReference,
  Product3DMaterialAssignment,
  Product3DPartOverride,
  Product3DReference,
  Product3DAssetBounds,
  Product3DDimensions,
  ThreeDMaterialDefaults,
} from "../features/floorplan3d/products/product3DTypes";

// Pattern fill types for walls and symbols.
// Keep legacy 'flooring' for backward compatibility with previously saved floorplans.
export type FillPattern =
  | "wood"
  | "woodContour"
  | "grass"
  | "sand"
  | "cement"
  | "concrete"
  | "stoneLarge"
  | "stoneSmall"
  | "earth"
  | "concreteBlock"
  | "steel"
  | "glass"
  | "rcc"
  | "paversOctagon"
  | "pavementGrid"
  | "basketweave"
  | "runningPavers"
  | "staggeredPavers"
  | "herringbone"
  | "herringbone2"
  | "chevron"
  | "chevronLines"
  | "tiles"
  | "parquet"
  | "flooring"
  | "brick"
  | "hatch45"
  | "crosshatch";

/**
 * Broad face of a wall relative to start→end (left = +Y in local wall frame).
 */
export type WallFaceSide = "left" | "right";

/**
 * Optional fill/material override for one whole-wall face.
 * In per-side mode, omitted surfaceRef means that side has no material
 * (no fallback to wall.threeD.surfaceRef). fillColor still falls back to
 * wall.fillColor when omitted.
 */
export type WallFaceMaterial = {
  surfaceRef?: CatalogSurfaceReference;
  fillColor?: number;
};

export type Wall3DMetadata = {
  height?: number;
  baseElevation?: number;
  materialId?: string;
  /** Whether the two broad wall faces use independent colors/materials. */
  differentSideColors?: boolean;
  /**
   * Whole-wall material. While differentSideColors is on this mirrors Side 1
   * (as do fillColor and materialId) so clients without per-side support
   * still render it; per-side rendering reads faceMaterials only.
   */
  surfaceRef?: CatalogSurfaceReference;
  /** Optional per-side materials for the full wall length. */
  faceMaterials?: {
    left?: WallFaceMaterial;
    right?: WallFaceMaterial;
  };
};

export type ShapePrimitiveKind =
  | "square"
  | "circle"
  | "triangle"
  | "pentagon"
  | "hexagon"
  | "octagon";

export type Shape3DPlacementMode = "floor" | "wall" | "free";

export type SmartTableChairModel = "chair" | "armchair";
export type SmartTableChairMode = "automatic" | "manual";

export type SmartTableChairSettings = {
  enabled: boolean;
  mode: SmartTableChairMode;
  model: SmartTableChairModel;
  /** Table-edge allocation per chair, in world metres. */
  seatSpacing: number;
};

export type SmartRectangleTableConfig = {
  version: 1;
  shape: "rectangle";
  width: number;
  length: number;
  chairs: SmartTableChairSettings & {
    automaticEnds: {
      start: boolean;
      end: boolean;
    };
    manualCounts: {
      left: number;
      right: number;
      start: number;
      end: number;
    };
  };
};

export type SmartRoundTableConfig = {
  version: 1;
  shape: "round";
  diameter: number;
  support: "pedestal" | "separateLegs";
  chairs: SmartTableChairSettings & {
    manualCount: number;
  };
};

export type SmartTableConfig =
  | SmartRectangleTableConfig
  | SmartRoundTableConfig;

/**
 * Configurable freestanding bookshelf.
 * `shelfCount` includes the top and bottom boards; `shelfSpacing` is the clear
 * opening between adjacent boards and drives overall height.
 */
export type BookShelfConfig = {
  version: 1;
  width: number;
  depth: number;
  height: number;
  shelfCount: number;
  shelfSpacing: number;
  /** Left/right side panels that form the outer frame. */
  hasFrame: boolean;
  hasBackPanel: boolean;
};

export type Symbol3DMetadata = {
  sourceMode?: "custom" | "primitiveOnly";
  /** Version of the built-in primitive dimensions used when this symbol was placed. */
  primitiveDefaultsVersion?: number;
  fillColorOverride?: boolean;
  height?: number;
  baseElevation?: number;
  topBulge?: number;
  modelKey?: string;
  shapeKind?: ShapePrimitiveKind;
  placementMode?: Shape3DPlacementMode;
  rotation3D?: {
    pitch?: number;
    yaw?: number;
    roll?: number;
  };
  wallMount?: {
    wallId: string;
    side?: "left" | "right";
    offset?: number;
    distanceFromWall?: number;
  };
  collision?: "box" | "none";
  dimensions?: {
    width?: number;
    height?: number;
    depth?: number;
  };
  materialAssignments?: Record<
    string,
    Product3DMaterialAssignment
  >;
  /** Asset-orientation corrections (radians) from the admin 3D defaults. */
  modelPitchOffset?: number;
  modelRollOffset?: number;
  modelYawOffset?: number;
  product?: Product3DReference;
  userModel?: {
    modelId: string;
    url: string;
    version?: string;
    status?: "ready";
    name?: string;
    thumbnailUrl?: string;
    /** Product photo (same preference as community library cards). */
    sourceImageUrl?: string;
    dimensions?: Product3DDimensions;
    bounds?: Product3DAssetBounds;
    sha256?: string;
    pitchOffset?: number;
    rollOffset?: number;
    yawOffset?: number;
    /** 2D plan silhouette shipped with the upload, before any 3D tilt. */
    footprintSvgPath?: string;
    /** Stamped commerce fields from the library row (Product List). */
    price?: number;
    currency?: string;
    urlStore?: string;
    brand?: string;
  };
  surfaceRef?: CatalogSurfaceReference;
  parts?: Record<string, Product3DPartOverride>;
  /**
   * Horizontal muntin (spröjs) bar count for 3D window assemblies.
   * Bars divide the glass into count + 1 vertical sections. Defaults to 0.
   */
  muntinHorizontalCount?: number;
  /**
   * Vertical muntin (spröjs) bar count for 3D window assemblies.
   * Bars divide the glass into count + 1 horizontal sections. Defaults to 0.
   */
  muntinVerticalCount?: number;
  /**
   * @deprecated Prefer {@link muntinHorizontalCount} / {@link muntinVerticalCount}.
   * Legacy single-axis muntin count for 3D window assemblies.
   */
  muntinCount?: number;
  /**
   * @deprecated Prefer {@link muntinHorizontalCount} / {@link muntinVerticalCount}.
   * Legacy muntin bar direction used with {@link muntinCount}.
   */
  muntinOrientation?: "horizontal" | "vertical";
  /**
   * 3D door leaf appearance. Defaults to flush when omitted.
   * See door3DStyle.ts for the shared enum / resolvers.
   */
  doorStyle?: "flush" | "paneled" | "glazed";
  /**
   * 3D door handle / knob style. Defaults to lever when omitted
   * (glass sliding doors prefer bar when unset).
   */
  doorHandleStyle?: "lever" | "knob" | "bar" | "none";
};

export type FloorplanCatalogDictionary = CatalogDictionary;
export type FloorplanThreeDMaterialDefaults = ThreeDMaterialDefaults;

export type FloorplanLightingSettings = {
  sunEnabled: boolean;
  sunTimeOfDay: number;
  roomLightsEnabled: boolean;
  /** Global 3D ceiling fill color (all room ceilings share this). */
  ceilingColor: number;
};

// Base type that contains the shared properties
export type SymbolType = {
  id: string;
  name?: string;
  type:
    | "door"
    | "slidingDoor"
    | "doubleDoor"
    | "window"
    | "symbol"
    | "circleStairs"
    | "rectStairs"
    | "turnStairs"
    | "square"
    | "triangle"
    | "circle"
    | "polygon"
    | "svg"
    | "text"
    | "area"
    | "areaFill"
    | "backdropImage"
    | "smartTable"
    | "bookShelf"
    | "void";
  position: THREE.Vector2;
  zIndex: number;
  clipWall?: boolean;
  rotation?: number;
  flipHorizontal?: boolean;
  flipVertical?: boolean;
  attachedTo?: string;
  attachedDistance?: number;
  lineType?: LineType;
  lineWidth?: number;
  lineWeight?: number;
  lineColor?: number;
  lineStyle?: number;
  fillColor?: number;
  /** Fill opacity from 0 (transparent) to 1 (opaque). */
  fillOpacity?: number;
  fillPattern?: FillPattern;
  hoverColor?: number;
  selectable?: boolean;
  selected?: boolean;
  libraryKey?: string;
  threeD?: Symbol3DMetadata;
  /** Image-trace import that produced this object (ungrouped inserts). */
  sourceImportId?: string;
  /** Which paste/copy of that import (0-based) for offset placement. */
  sourceImportCopyIndex?: number;
};

// DoorType extends SymbolType and adds its unique properties
export type DoorType = SymbolType & {
  //start: THREE.Vector2;
  //end: THREE.Vector2;
  doorWidth: number;
  openAngle: number;
  doorFrameWidth?: number;
  doorBladeThickness?: number;
  /** When true, opening width and 3D height stay proportional (off by default). */
  lockAspectRatio?: boolean;
  ratio?: number;
};
/** Sliding door — same opening props as DoorType; openAngle is a 0–1 slide fraction. */
export type SlidingDoorType = SymbolType & {
  doorWidth: number;
  openAngle: number;
  doorFrameWidth?: number;
  doorBladeThickness?: number;
  lockAspectRatio?: boolean;
  ratio?: number;
};
// DoubleDoorType extends SymbolType and adds its unique properties
export type DoubleDoorType = SymbolType & {
  //start: THREE.Vector2;
  //end: THREE.Vector2;
  doubleDoorWidth: number;
  openAngle: number;
  doorFrameWidth?: number;
  doorBladeThickness?: number;
  lockAspectRatio?: boolean;
  ratio?: number;
};
// WindowType extends SymbolType and adds its unique properties
export type WindowType = SymbolType & {
  windowWidth: number;
  windowLength: number;
  windowFrameWidth?: number;
  openAngle?: number;
  /** When true, opening width and 3D height stay proportional (off by default). */
  lockAspectRatio?: boolean;
  ratio?: number;
  /**
   * Horizontal muntin (spröjs) bar count for 3D assemblies.
   * Bars divide the glass into count + 1 vertical sections. Defaults to 0.
   */
  muntinHorizontalCount?: number;
  /** Alias for {@link WindowType.muntinHorizontalCount} (Swedish). */
  spröjsHorizontalCount?: number;
  /**
   * Vertical muntin (spröjs) bar count for 3D assemblies.
   * Bars divide the glass into count + 1 horizontal sections. Defaults to 0.
   */
  muntinVerticalCount?: number;
  /** Alias for {@link WindowType.muntinVerticalCount} (Swedish). */
  spröjsVerticalCount?: number;
  /**
   * @deprecated Prefer {@link WindowType.muntinHorizontalCount} /
   * {@link WindowType.muntinVerticalCount}.
   * Legacy single-axis muntin count.
   */
  muntinCount?: number;
  /** Alias for {@link WindowType.muntinCount} (Swedish). */
  spröjsCount?: number;
  /**
   * @deprecated Prefer {@link WindowType.muntinHorizontalCount} /
   * {@link WindowType.muntinVerticalCount}.
   * Legacy muntin bar direction used with {@link WindowType.muntinCount}.
   */
  muntinOrientation?: "horizontal" | "vertical";
  /** Alias for {@link WindowType.muntinOrientation} (Swedish). */
  spröjsOrientation?: "horizontal" | "vertical";
};
// VoidType extends SymbolType and adds its unique properties
export type VoidType = SymbolType & {
  voidWidth: number;
  voidLength: number;
};
// CircleStairsType extends SymbolType and adds its unique properties
export type CircleStairsType = SymbolType & {
  //start: THREE.Vector2;
  //end: THREE.Vector2;
  circleStairsWidth: number;
  openAngle: number;
  stairStepSize?: number;
};
// RectStairsType extends SymbolType and adds its unique properties
export type RectStairsType = SymbolType & {
  //start: THREE.Vector2;
  //end: THREE.Vector2;
  rectStairsWidth: number;
  rectStairsHeight: number;
  openAngle: number;
  stairStepSize?: number;
  /** Unused for stairs — width and length stay independent. */
  lockAspectRatio?: boolean;
  ratio?: number;
};

/** Plan shape for compound turn stairs (L = 90°, U = 180° switchback). */
export type StairShape = "L" | "U";
/** Turn direction relative to the first flight's travel direction. */
export type StairTurnDirection = "left" | "right";

/**
 * L- or U-shaped stairs with two flights and a required intermediate landing.
 * Flight lengths are controlled by step counts; tread/riser follow stairStepSize.
 */
export type TurnStairsType = SymbolType & {
  type: "turnStairs";
  stairShape: StairShape;
  turnDirection: StairTurnDirection;
  stairWidth: number;
  firstFlightSteps: number;
  secondFlightSteps: number;
  /**
   * Landing depth along the first flight axis (U-shape only).
   * L-shape landings are derived from stairWidth (square corner pad).
   * Defaults to stairWidth when unset at insert.
   */
  landingDepth: number;
  stairStepSize?: number;
};

// SquareSymbolType extends SymbolType and adds its unique properties
export type SquareSymbolType = SymbolType & {
  //start: THREE.Vector2;
  //end: THREE.Vector2;
  squareWidth: number;
  squareHeight: number;
  openAngle: number;
  lockAspectRatio?: boolean;
  ratio?: number;
};
// TriangleSymbolType extends SymbolType and adds its unique properties
export type TriangleSymbolType = SymbolType & {
  //start: THREE.Vector2;
  //end: THREE.Vector2;
  triangleWidth: number;
  triangleHeight: number;
  openAngle: number;
  lockAspectRatio?: boolean;
  ratio?: number;
};
// circleSymbolType extends SymbolType and adds its unique properties
export type CircleSymbolType = SymbolType & {
  //start: THREE.Vector2;
  //end: THREE.Vector2;
  circleWidth: number;
  circleHeight: number;
  circleRadius: number;
  openAngle: number;
  lockAspectRatio?: boolean;
  ratio?: number;
};
export type PolygonSymbolType = SymbolType & {
  type: "polygon";
  polygonKind: Exclude<ShapePrimitiveKind, "square" | "circle" | "triangle">;
  polygonSides: 5 | 6 | 8;
  polygonWidth: number;
  polygonHeight: number;
  openAngle: number;
  lockAspectRatio?: boolean;
  ratio?: number;
};
export type SvgType = SymbolType & {
  //start: THREE.Vector2;
  //end: THREE.Vector2;
  svgPath: string;
  svgLength: number; // Length X-axis
  svgHeight: number; // Width Y-axis
  svgRatio?: number;
  lockAspectRatio?: boolean;
  ratio?: number;
  flipped?: boolean; // Whether the SVG should be flipped (for backward compatibility)
};
export type SmartTableType = Omit<SymbolType, "type"> & {
  type: "smartTable";
  smartTable: SmartTableConfig;
};
export type BookShelfType = Omit<SymbolType, "type"> & {
  type: "bookShelf";
  bookShelf: BookShelfConfig;
};
export type TextType = SymbolType & {
  //start: THREE.Vector2;
  //end: THREE.Vector2;

  textBoxWidth: number; // Scale X of the text object (1 = original size)
  textBoxHeight: number; // Scale Y of the text object (1 = original size)
  wrapWidth?: number; // Width for word wrapping (in world units), controlled by middle handles
  fontSize: number;
  fontStyle: string;
  fontWeight: string; // A numeric font weight, "normal", or "bold"
  fontFamily: string; // Font family name (e.g., "Inter", "sans-serif")
  textAlign: "left" | "center" | "right"; // Horizontal text alignment
  lineHeight: number;
  text: string;
};
export type BackdropImageType = SymbolType & {
  backdropImagePath: string;
  backdropAlphaMask?: string;
  backdropRatio?: number;
  backdropSize?: number;
  /**
   * When true (default on create), the image cannot be selected or dragged on
   * the canvas — only via Drawings & images / the property panel. Unlock to
   * click and drag it in the workspace.
   */
  locked?: boolean;
  /** Opacity from 0 (transparent) to 1 (opaque). Missing means 0.8 (legacy default). */
  opacity?: number;
  /** When false the image is hidden but kept in the plan. Missing means visible. */
  visible?: boolean;
};
export type AreaSymbolType = SymbolType & {
  type: "area";
  vertices: THREE.Vector2[];
};
export type AreaFillSymbolType = SymbolType & {
  type: "areaFill";
  vertices: THREE.Vector2[];
  /** When true (default), the fill cannot be dragged or reshaped. Missing means locked. */
  locked?: boolean;
  roomBinding?: {
    roomId?: string;
    roomSignature: string;
    objectIds: string[];
    autoCreated?: boolean;
  };
};
// Type guard to check if the symbol is of type SymbolType
export function isSymbolType(symbol: any): symbol is SymbolType {
  return (
    [
      "door",
      "slidingDoor",
      "doubleDoor",
      "window",
      "symbol",
      "circleStairs",
      "rectStairs",
      "turnStairs",
      "square",
      "triangle",
      "circle",
      "polygon",
      "svg",
      "text",
      "area",
      "areaFill",
      "backdropImage",
      "smartTable",
      "bookShelf",
      "void",
    ].find((type) => (symbol as SymbolType).type === type) !== undefined
  );
}
// Type guard to check if the symbol is of type DoorType
export function isDoorType(symbol: any): symbol is DoorType {
  return symbol && (symbol as DoorType).type === "door";
}
/** Type guard for sliding doors (openAngle is slide fraction 0–1). */
export function isSlidingDoorType(symbol: any): symbol is SlidingDoorType {
  return symbol && (symbol as SlidingDoorType).type === "slidingDoor";
}
/**
 * Hinged or sliding single-leaf openings that use doorWidth / doorFrameWidth.
 * Use this for wall clipping, attachment geometry, and selection bounds.
 */
export function isSingleLeafDoorType(
  symbol: any,
): symbol is DoorType | SlidingDoorType {
  return isDoorType(symbol) || isSlidingDoorType(symbol);
}
// Type guard to check if the symbol is of type DoubleDoorType
export function isDoubleDoorType(symbol: any): symbol is DoubleDoorType {
  return symbol && (symbol as DoubleDoorType).type === "doubleDoor";
}
// Type guard to check if the symbol is of type WindowType
export function isWindowType(symbol: any): symbol is WindowType {
  return symbol && (symbol as WindowType).type === "window";
}
export function isSmartTableType(symbol: any): symbol is SmartTableType {
  return symbol && (symbol as SmartTableType).type === "smartTable";
}
export function isBookShelfType(symbol: any): symbol is BookShelfType {
  return symbol && (symbol as BookShelfType).type === "bookShelf";
}
// Type guard to check if the symbol is of type VoidType
export function isVoidType(symbol: any): symbol is VoidType {
  return symbol && (symbol as VoidType).type === "void";
}
// Type guard to check if the symbol is of type circleStairsType
export function isCircleStairsType(symbol: any): symbol is CircleStairsType {
  return symbol && (symbol as CircleStairsType).type === "circleStairs";
}
// Type guard to check if the symbol is of type rectStairsType
export function isRectStairsType(symbol: any): symbol is RectStairsType {
  return symbol && (symbol as RectStairsType).type === "rectStairs";
}
/** Type guard for L/U turn stairs. */
export function isTurnStairsType(symbol: any): symbol is TurnStairsType {
  return symbol && (symbol as TurnStairsType).type === "turnStairs";
}

// Type guard to check if the symbol is of type squareSymbolType
export function isSquareSymbolType(symbol: any): symbol is SquareSymbolType {
  return symbol && (symbol as SquareSymbolType).type === "square";
}
// Type guard to check if the symbol is of type triangleSymbolType
export function isTriangleSymbolType(
  symbol: any,
): symbol is TriangleSymbolType {
  return symbol && (symbol as TriangleSymbolType).type === "triangle";
}
// Type guard to check if the symbol is of type circleSymbolType
export function isCircleSymbolType(symbol: any): symbol is CircleSymbolType {
  return symbol && (symbol as CircleStairsType).type === "circle";
}
export function isPolygonSymbolType(symbol: any): symbol is PolygonSymbolType {
  return symbol && (symbol as PolygonSymbolType).type === "polygon";
}
// Type guard to check if the symbol is of type SvgType
export function isSvgType(symbol: any): symbol is SvgType {
  return symbol && (symbol as SvgType).type === "svg";
}
// Type guard to check if the symbol is of type TextType
export function isTextType(symbol: any): symbol is TextType {
  return symbol && (symbol as TextType).type === "text";
}
// Type guard to check if the symbol is of type backdropImageType
export function isBackdropImageType(symbol: any): symbol is BackdropImageType {
  return symbol && (symbol as BackdropImageType).type === "backdropImage";
}

// Type guard to check if the object is of type WallType
export function isWallType(object: any): object is WallType {
  return object && (object as WallType).type === "wall";
}
// Type guard to check if the object is of type SingleLineType
export function isSingleLineType(object: any): object is SingleLineType {
  return object && (object as SingleLineType).type === "singleLine";
}
// Type guard to check if the object is of type RulerLineType
export function isRulerLineType(object: any): object is RulerLineType {
  return object && (object as RulerLineType).type === "rulerLine";
}
export function isRuler3DType(object: any): object is Ruler3DType {
  return object && (object as Ruler3DType).type === "ruler3D";
}
export function isAreaSymbolType(symbol: any): symbol is AreaSymbolType {
  return symbol && (symbol as AreaSymbolType).type === "area";
}
export function isAreaFillSymbolType(
  symbol: any,
): symbol is AreaFillSymbolType {
  return symbol && (symbol as AreaFillSymbolType).type === "areaFill";
}

/**
 * Room floor fills are locked by default (including older plans that omit `locked`).
 * Only an explicit `locked: false` allows dragging / vertex edits.
 */
export function isAreaFillMovementLocked(
  symbol: Pick<AreaFillSymbolType, "locked">,
): boolean {
  return symbol.locked !== false;
}
export type CutoutType = {
  positionFromStart: number;
  length: number;
  // appliesTo is a set of 'left', 'right', 'inner', 'auto-start', 'auto-end'
  appliesTo: Set<string>;
  belongsTo?: string;
};

export type ClippingPolygonType = {
  belongsTo: string;
  polygon: THREE.Vector2[];
  objectType: "wall" | "door" | "slidingDoor" | "doubleDoor" | "window" | "symbol" | "void";
};

export enum LineType {
  solid = "solid",
  dashed = "dashed",
}

export const WallConnectionEnd = 10000;
export const WallConnectionStart = 0;
export const WallConnectionMiddle = 5000;

export type WallConnectionType = {
  id: string;
  sourcePosition: number;
  targetPosition: number;
  mode?: "edge" | "center";
};

export type SymbolAttachmentType = {
  symbolId: string;
  positionFromStart: number;
};

export type WallType = {
  id: string;
  name?: string;
  type: "wall";
  start: THREE.Vector2;
  end: THREE.Vector2;
  controlPoint?: THREE.Vector2;
  position?: [number, number];
  centerConnections?: WallConnectionType[];
  edgeConnections?: WallEdgeConnection[];
  symbolAttachments?: SymbolAttachmentType[];
  cutouts?: CutoutType[];
  lineType?: LineType | "solid";
  lineWidth?: number;
  lineWeight?: number;
  wallWidth?: number;
  selectable?: boolean;
  selected?: boolean;
  lineColor?: number;
  fillColor?: number;
  /** Fill opacity from 0 (transparent) to 1 (opaque). */
  fillOpacity?: number;
  fillPattern?: FillPattern;
  hoverColor?: number;
  lineStyle?: string;
  lineForm?: "straight" | "arc";
  lastUpdate?: number;
  roomIds?: string[];
  hideMeasurement?: boolean;
  cornerState?: WallCornerPersistedMap;
  threeD?: Wall3DMetadata;
  geometryVersion?: number; // Serial number that increments when wall geometry changes (position, width, connections, etc.)
  /** Image-trace import that produced this object (ungrouped inserts). */
  sourceImportId?: string;
  /** Which paste/copy of that import (0-based) for offset placement. */
  sourceImportCopyIndex?: number;
};

export type SingleLineType = {
  id: string;
  name?: string;
  type: "singleLine";
  start: THREE.Vector2;
  end: THREE.Vector2;
  controlPoint?: THREE.Vector2;
  position?: [number, number];
  centerConnections?: WallConnectionType[];
  lineType?: LineType;
  lineWeight?: number;
  selectable?: boolean;
  selected?: boolean;
  lineColor?: number;
  fillColor?: number;
  hoverColor?: number;
  lineStyle?: string;
  lineForm?: "straight" | "arc";
  lastUpdate?: number;
  roomIds?: string[];
  hideMeasurement?: boolean;
  zIndex?: number;
  /** Image-trace import that produced this object (ungrouped inserts). */
  sourceImportId?: string;
  /** Which paste/copy of that import (0-based) for offset placement. */
  sourceImportCopyIndex?: number;
};
export type RulerLineType = {
  id: string;
  name?: string;
  type: "rulerLine";
  start: THREE.Vector2;
  end: THREE.Vector2;
  controlPoint?: THREE.Vector2;
  position?: [number, number];
  centerConnections?: WallConnectionType[];
  lineType?: LineType;
  lineWeight?: number;
  selectable?: boolean;
  selected?: boolean;
  lineColor?: number;
  fillColor?: number;
  hoverColor?: number;
  lineStyle?: string;
  lineForm?: "straight" | "arc";
  lastUpdate?: number;
  hideMeasurement?: boolean;
  /** Image-trace import that produced this object (ungrouped inserts). */
  sourceImportId?: string;
  /** Which paste/copy of that import (0-based) for offset placement. */
  sourceImportCopyIndex?: number;
};
export type Ruler3DType = {
  id: string;
  name?: string;
  type: "ruler3D";
  start: THREE.Vector3;
  end: THREE.Vector3;
  measurementMode: "vertical" | "free";
  selectable?: boolean;
  selected?: boolean;
  lineWeight?: number;
  lineColor?: number;
  hoverColor?: number;
  lineStyle?: string;
  lastUpdate?: number;
  hideMeasurement?: boolean;
};
export type FloorplannerSelectionType =
  | WallType
  | SymbolType
  | SingleLineType
  | RulerLineType
  | Ruler3DType;
export interface Point {
  x: number;
  y: number;
}
export type LineSideType = "left" | "right" | "inner" | "endcap";

export type WallCornerKey =
  | "topLeft"
  | "topRight"
  | "bottomRight"
  | "bottomLeft";

export interface PersistedCorner {
  s?: number;
  t?: number;
  x?: number;
  y?: number;
}

export type WallCornerPersistedMap = Partial<
  Record<WallCornerKey, PersistedCorner>
>;

export interface WallCornerDescriptor {
  key: WallCornerKey;
  point: THREE.Vector2;
  /**
   * Indices into the shape's paths array that correspond to this corner.
   * Multiple indices allow the same logical corner to be represented
   * in both outline and fill paths when necessary.
   */
  pathIndices?: number[];
}

export type WallCornerMap = Partial<
  Record<WallCornerKey, WallCornerDescriptor>
>;

export type WallShapeType = {
  wall: WallType;
  paths: THREE.Vector2[];
  lineSide: LineSideType;
  /**
   * Optional named corner descriptors that describe the logical corners
   * of the wall independent of the order of the raw path vertices.
   */
  corners?: WallCornerMap;
  /**
   * Optional serialization marker. When true, indicates this shape originated
   * from legacy geometry that did not provide explicit corner descriptors.
   */
  legacyCorners?: boolean;
};

export type otherModifiedPath = {
  wallId: string;
  pathIndex: number;
  point: THREE.Vector2;
};

export type Graph = {
  [key: string]: Point[];
};

export type Room = Point[];

export type AlignmentLineType = {
  id: string;
  hit: Point;
  axis: "x" | "y";
  color: string;
  lineWidth: number;
  objectId: string;
  mode?: "center" | "edge";
  /** The source object's point that generated this alignment line, used for prioritization */
  originPoint?: Point;
};

export type RoomType = {
  id: string;
  name?: string;
  type: "room";
  objectIds: string[];
  vertices?: THREE.Vector2[];
  centroid: THREE.Vector2;
  areaWorldUnits: number;
  selectable?: boolean;
  selected?: boolean;
  lineColor?: number;
  fillColor?: number;
  hoverColor?: number;
};

export const zIndexEpsilon = 0.001;

export type GroupType = {
  id: string;
  name?: string;
  type: "group";
  objectIds: string[];
  created?: number;
  sourceImportId?: string;
};

// Type guard to check if the object is of type GroupType
export function isGroupType(object: any): object is GroupType {
  return object && (object as GroupType).type === "group";
}

/**
 * Interface representing a point on a wall edge for edge-based connections.
 * Walls connect at their edges (perpendicular to wall direction) rather than centers,
 * preserving wall lengths and creating more predictable UX.
 */
export interface WallEdgePoint {
  wallId: string;
  endpoint: "start" | "end" | "middle";
  edgePosition: THREE.Vector2;
  edgeNormal: THREE.Vector2;
  side: "left" | "right";
}

/**
 * Interface representing an edge-to-edge connection between two walls.
 * Maintains the relationship between walls connected at their edges with gap information
 * for proper rendering of fill polygons.
 */
export interface WallEdgeConnection {
  id: string;
  sourceWallId: string;
  targetWallId: string;
  sourceEndpoint: "start" | "end";
  targetEndpoint: "start" | "end" | "middle";
  sourceSide?: "left" | "right";
  targetSide?: "left" | "right";
  sourceEdgePoint: THREE.Vector2;
  targetEdgePoint: THREE.Vector2;
  gapDistance: number;
  connectionAngle: number;
  voidFill?: VoidFillMetadata;
}

/**
 * Type guard to distinguish edge-based connections from center-based connections.
 * Edge connections have sourceEdgePoint and targetEdgePoint properties.
 */
export function isEdgeConnection(
  connection: any,
): connection is WallEdgeConnection {
  return (
    connection &&
    (connection as WallEdgeConnection).sourceEdgePoint !== undefined &&
    (connection as WallEdgeConnection).targetEdgePoint !== undefined
  );
}

/**
 * Individual vertex in a joint polygon with metadata about which wall it belongs to.
 */
export type JointPolygonVertex = {
  position: THREE.Vector2;
  wallId: string;
  edgePoint: boolean;
};

/**
 * Data structure for joint polygons that fill gaps between edge-connected walls.
 * These polygons are rendered separately from wall geometry to ensure visual continuity
 * at wall junctions without modifying the underlying wall shapes.
 */
/**
 * A single gap-fill polygon for a joint, with optional holes. Joints can produce
 * several disjoint fill islands (e.g. an X-junction), hence an array of these.
 */
export type JointFillPolygon = {
  outer: THREE.Vector2[];
  holes: THREE.Vector2[][];
};

export type JointPolygonData = {
  id: string;
  jointCenter: THREE.Vector2;
  /**
   * Backward-compatible single-ring representation (outer ring of the largest
   * fill island). Prefer `fillPolygons` for accurate multi-island/holed fills.
   */
  vertices: THREE.Vector2[];
  /** Gap-only fill polygons from the boolean joint solid (may be empty). */
  fillPolygons?: JointFillPolygon[];
  /**
   * Local 2D joint cap polygons from Boolean coverage >= 2. These may overlap
   * wall footprints, so renderers must not use them as visible fill. They are
   * kept for diagnostics and geometry tests. Visible 2D and 3D fill must use
   * fillPolygons.
   */
  coverPolygons?: JointFillPolygon[];
  wallIds: string[];
  /** Wall whose fill/outline style owns the joint fill. */
  primaryWallId?: string;
  fillColor: number;
  fillOpacity?: number;
  fillPattern?: FillPattern;
  zIndex: number;
  edgeConnections: WallEdgeConnection[];
  isVoidFill?: boolean;
  outlineColor?: number;
  outlineWidth?: number;
  outlineDashed?: boolean;
  outlineSegments?: JointPolygonOutlineSegment[];
  voidFillMetadata?: VoidFillMetadata;
};

export type JointPolygonOutlineSegment = {
  start: THREE.Vector2;
  end: THREE.Vector2;
  color: number;
  width: number;
  dashed: boolean;
  wallId?: string;
};

/**
 * Individual line segment for wall outlines with gap information.
 * These segments are pre-calculated and stored to avoid repeated computation during rendering.
 */
export type OutlineSegment = {
  id: string;
  wallId: string;
  side: "left" | "right";
  start: THREE.Vector2;
  end: THREE.Vector2;
  thickness: number;
  color: number;
  dashed: boolean;
  gapReason?: "edgeConnection" | "midConnection" | "cutout";
};

/**
 * Collection of outline segments for all sides of a wall.
 * Segments include left/right outlines and endcap segments.
 */
export type OutlineSegmentCollection = {
  wallId: string;
  leftSegments: OutlineSegment[];
  rightSegments: OutlineSegment[];
  endCapSegments: OutlineSegment[];
  geometryVersion: number;
};

export type VoidFillMetadata = {
  required: boolean;
  ownerWallId: string;
  fillColor: number;
  outlineColor: number;
  outlineWidth: number;
  outlineDashed?: boolean;
  reason: "single-corner";
  resolvedTargetEndpoint?: "start" | "end" | "middle";
};
